import React, { useState, useRef, useEffect, useMemo } from 'react';
import api from '../services/api';
import FormularioDownloadButton from './FormularioDownloadButton';
import { useTheme } from '../contexts/ThemeContext';

interface TemaTokensWidget {
  fundo: string;
  fundoSidebar: string;
  fundoMensagens: string;
  borda: string;
  texto: string;
  textoSecundario: string;
  textoTerciario: string;
  bolhaAssistente: string;
  cardFundo: string;
  cardBorda: string;
  inputFundo: string;
  inputBorda: string;
  chipFundo: string;
  hoverItem: string;
  botaoNeutro: string;
}

function obterTokensWidget(tema: 'claro' | 'escuro'): TemaTokensWidget {
  if (tema === 'claro') {
    return {
      fundo: '#ffffff',
      fundoSidebar: '#f7f7f7',
      fundoMensagens: '#f5f5f5',
      borda: '#e8e8e8',
      texto: '#1a1a1a',
      textoSecundario: '#555',
      textoTerciario: '#999',
      bolhaAssistente: '#eeeeee',
      cardFundo: '#ffffff',
      cardBorda: '#e0e0e0',
      inputFundo: '#ffffff',
      inputBorda: '#ddd',
      chipFundo: '#eee',
      hoverItem: '#efefef',
      botaoNeutro: '#ccc',
    };
  }
  return {
    fundo: '#1a1a1a',
    fundoSidebar: '#161616',
    fundoMensagens: '#161616',
    borda: '#2a2a2a',
    texto: '#eee',
    textoSecundario: '#ccc',
    textoTerciario: '#888',
    bolhaAssistente: '#2a2a2a',
    cardFundo: '#2a2a2a',
    cardBorda: '#333',
    inputFundo: '#222',
    inputBorda: '#444',
    chipFundo: '#333',
    hoverItem: '#222',
    botaoNeutro: '#444',
  };
}

interface Mensagem {
  role: 'user' | 'assistant';
  content: string;
}

interface Conversa {
  id: number;
  titulo: string;
  atualizado_em: string;
}

interface TarefaDados {
  titulo: string;
  descricao: string;
  prioridade: string;
  responsavel_nome: string;
  data_vencimento: string;
  quer_anexar?: boolean;
}

interface MensagemParsed {
  texto: string;
  tarefa: TarefaDados | null;
}

function parseMensagem(content: string): MensagemParsed {
  const match = content.match(/\[CRIAR_TAREFA\]([\s\S]*?)\[\/CRIAR_TAREFA\]/);
  if (!match) return { texto: content, tarefa: null };
  const texto = content.replace(/\[CRIAR_TAREFA\][\s\S]*?\[\/CRIAR_TAREFA\]/, '').trim();
  try {
    const tarefa: TarefaDados = JSON.parse(match[1].trim());
    return { texto, tarefa };
  } catch {
    return { texto: content, tarefa: null };
  }
}

// Extrai bloco [FORMULARIO] do conteúdo
function parseFormulario(content: string): { texto: string; nomeFormulario: string | null } {
  const match = content.match(/\[FORMULARIO\]([\s\S]*?)\[\/FORMULARIO\]/);
  if (!match) return { texto: content, nomeFormulario: null };
  const texto = content.replace(/\[FORMULARIO\][\s\S]*?\[\/FORMULARIO\]/, '').trim();
  try {
    const dados = JSON.parse(match[1].trim());
    return { texto, nomeFormulario: dados.nome || null };
  } catch {
    return { texto: content, nomeFormulario: null };
  }
}

function renderMarkdown(text: string): React.ReactNode {
  const linhas = text.split('\n');
  return linhas.map((linha, li) => {
    const partes = linha.split(/\*\*(.*?)\*\*/g);
    return (
      <React.Fragment key={li}>
        {partes.map((p, i) =>
          i % 2 === 1 ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>
        )}
        {li < linhas.length - 1 && <br />}
      </React.Fragment>
    );
  });
}


function formatarData(iso: string): string {
  const d = new Date(iso);
  const hoje = new Date();
  const diff = Math.floor((hoje.getTime() - d.getTime()) / 86400000);
  if (diff === 0) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (diff === 1) return 'Ontem';
  if (diff < 7) return `${diff} dias atrás`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

const prioridadeLabel: Record<string, string> = {
  baixa: '🟢 Baixa',
  media: '🟡 Média',
  alta: '🟠 Alta',
  urgente: '🔴 Urgente',
};

const BravoAI: React.FC = () => {
  const { tema } = useTheme();
  const t = useMemo(() => obterTokensWidget(tema), [tema]);
  const [aberto, setAberto] = useState(false);
  const [mostrarSidebar, setMostrarSidebar] = useState(false);
  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [conversaAtualId, setConversaAtualId] = useState<number | null>(null);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [input, setInput] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [carregandoConversa, setCarregandoConversa] = useState(false);
  const [criandoTarefa, setCriandoTarefa] = useState(false);
  const [tarefaCriada, setTarefaCriada] = useState<string | null>(null);
  const [arquivosSelecionados, setArquivosSelecionados] = useState<File[]>([]);
  const [hoveredConversaId, setHoveredConversaId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const conversasCarregadas = useRef(false);

  // Drag state
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef({ dragging: false, moved: false, startX: 0, startY: 0, startBtnX: 0, startBtnY: 0 });

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const BTN_W = 140;
    const BTN_H = 48;
    const currentX = pos?.x ?? (window.innerWidth - BTN_W - 24);
    const currentY = pos?.y ?? (window.innerHeight - BTN_H - 24);
    dragRef.current = { dragging: true, moved: false, startX: e.clientX, startY: e.clientY, startBtnX: currentX, startBtnY: currentY };

    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d.dragging) return;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) d.moved = true;
      document.body.style.cursor = 'grabbing';
      setPos({
        x: Math.max(8, Math.min(window.innerWidth - BTN_W - 8, d.startBtnX + dx)),
        y: Math.max(8, Math.min(window.innerHeight - BTN_H - 8, d.startBtnY + dy)),
      });
    };

    const onUp = () => {
      document.body.style.cursor = '';
      if (!dragRef.current.moved) setAberto(prev => !prev);
      dragRef.current.dragging = false;
      dragRef.current.moved = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  useEffect(() => {
    if (aberto && !conversasCarregadas.current) {
      conversasCarregadas.current = true;
      carregarListaConversas().then(lista => {
        if (lista.length > 0) abrirConversa(lista[0].id);
      });
    }
  }, [aberto]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens, carregando]);

  useEffect(() => {
    if (aberto && !carregandoConversa) inputRef.current?.focus();
  }, [aberto, carregandoConversa, mensagens]);

  const carregarListaConversas = async (): Promise<Conversa[]> => {
    try {
      const res = await api.get('/chat/conversas');
      if (res.data.success) {
        setConversas(res.data.conversas);
        return res.data.conversas;
      }
    } catch {}
    return [];
  };

  const abrirConversa = async (id: number) => {
    setCarregandoConversa(true);
    setConversaAtualId(id);
    setTarefaCriada(null);
    setArquivosSelecionados([]);
    try {
      const res = await api.get(`/chat/conversas/${id}`);
      if (res.data.success) setMensagens(res.data.mensagens);
    } catch {}
    setCarregandoConversa(false);
  };

  const novaConversa = () => {
    setMensagens([]);
    setConversaAtualId(null);
    setTarefaCriada(null);
    setArquivosSelecionados([]);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const excluirConversa = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.delete(`/chat/conversas/${id}`);
      const novaLista = conversas.filter(c => c.id !== id);
      setConversas(novaLista);
      if (conversaAtualId === id) {
        if (novaLista.length > 0) abrirConversa(novaLista[0].id);
        else novaConversa();
      }
    } catch {}
  };

  const enviarMensagem = async () => {
    const texto = input.trim();
    if (!texto || carregando) return;

    const novaMensagem: Mensagem = { role: 'user', content: texto };
    const novaLista = [...mensagens, novaMensagem];
    setMensagens([...novaLista, { role: 'assistant', content: '' }]);
    setInput('');
    setCarregando(true);
    setTarefaCriada(null);

    try {
      const token = localStorage.getItem('bravobet_token') || '';
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ mensagem: texto, historico: novaLista.slice(-20), conversa_id: conversaAtualId }),
      });

      if (!response.ok || !response.body) throw new Error('Stream falhou');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let respostaCompleta = '';
      let novoConversaId: number | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const data = trimmed.slice(6);
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.done) { novoConversaId = parsed.conversa_id; continue; }
            if (parsed.token) {
              respostaCompleta += parsed.token;
              // Oculta bloco CRIAR_TAREFA durante o streaming
              const visivel = respostaCompleta.includes('[CRIAR_TAREFA]')
                ? respostaCompleta.split('[CRIAR_TAREFA]')[0]
                : respostaCompleta;
              setMensagens(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: visivel };
                return updated;
              });
            }
          } catch {}
        }
      }

      // Atualizar mensagem final com conteúdo completo (parsed)
      setMensagens(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: respostaCompleta };
        return updated;
      });

      // Atualizar lista de conversas
      if (novoConversaId && !conversaAtualId) {
        setConversaAtualId(novoConversaId);
        setConversas(prev => [{
          id: novoConversaId!,
          titulo: texto.slice(0, 60) + (texto.length > 60 ? '...' : ''),
          atualizado_em: new Date().toISOString(),
        }, ...prev]);
      } else if (novoConversaId) {
        setConversas(prev => {
          const atual = prev.find(c => c.id === novoConversaId);
          if (!atual) return prev;
          return [{ ...atual, atualizado_em: new Date().toISOString() }, ...prev.filter(c => c.id !== novoConversaId)];
        });
      }

    } catch {
      setMensagens(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: 'Não foi possível conectar à Bravo AI. Tente novamente em instantes.' };
        return updated;
      });
    } finally {
      setCarregando(false);
    }
  };

  const confirmarCriarTarefa = async (tarefa: TarefaDados) => {
    setCriandoTarefa(true);
    setTarefaCriada(null);
    try {
      const usersRes = await api.get('/usuarios/dropdown');
      const usuarios: { id: string; nome: string; departamento_id: string }[] = usersRes.data.usuarios || [];

      const responsavel = usuarios.find(u => {
        const nomeCompleto = u.nome.toLowerCase();
        const nomeBuscado = tarefa.responsavel_nome.toLowerCase();
        if (nomeCompleto.includes(nomeBuscado) || nomeBuscado.includes(nomeCompleto)) return true;
        // verifica se todas as palavras do nome buscado aparecem no nome completo
        const palavras = nomeBuscado.split(/\s+/).filter(p => p.length > 1);
        return palavras.length > 0 && palavras.every(p => nomeCompleto.includes(p));
      });

      if (!responsavel) {
        setTarefaCriada(`erro:Responsável "${tarefa.responsavel_nome}" não encontrado no sistema. Verifique o nome e tente novamente.`);
        return;
      }

      const tarefaRes = await api.post('/tarefas', {
        titulo: tarefa.titulo,
        descricao: tarefa.descricao || '',
        prioridade: tarefa.prioridade,
        responsavel_id: responsavel.id,
        departamento_id: responsavel.departamento_id,
        data_vencimento: tarefa.data_vencimento || null,
        status: 'Solicitado',
      });

      if (arquivosSelecionados.length > 0) {
        const tarefaId = tarefaRes.data?.tarefa?.id || tarefaRes.data?.id;
        if (tarefaId) {
          for (const arquivo of arquivosSelecionados) {
            const formData = new FormData();
            formData.append('arquivo', arquivo);
            await api.post(`/anexos/tarefa/${tarefaId}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }
        }
      }

      setTarefaCriada('ok');
      setArquivosSelecionados([]);
      const msgAnexos = arquivosSelecionados.length > 0 ? ` com ${arquivosSelecionados.length} anexo(s)` : '';
      setMensagens(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `✅ Tarefa **"${tarefa.titulo}"** criada com sucesso${msgAnexos} e enviada para **${tarefa.responsavel_nome}**! Você pode acompanhar em "Tarefas Enviadas".`,
        },
      ]);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Erro ao criar tarefa. Tente novamente.';
      setTarefaCriada(`erro:${msg}`);
    } finally {
      setCriandoTarefa(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  };

  const ultimaTarefaIdx = (() => {
    for (let i = mensagens.length - 1; i >= 0; i--) {
      if (mensagens[i].role === 'assistant') {
        const p = parseMensagem(mensagens[i].content);
        if (p.tarefa) return i;
      }
    }
    return -1;
  })();

  return (
    <>
      {aberto && (() => {
        const chatW = mostrarSidebar ? 570 : 370;
        const chatH = 560;
        const BTN_W = 140;
        const BTN_H = 48;
        let chatPos: React.CSSProperties;
        if (pos) {
          const top = pos.y - chatH - 8 >= 8
            ? pos.y - chatH - 8
            : pos.y + BTN_H + 8;
          let left = pos.x + BTN_W - chatW;
          left = Math.max(8, Math.min(window.innerWidth - chatW - 8, left));
          chatPos = { top, left };
        } else {
          chatPos = { bottom: '88px', right: '24px' };
        }
        return (
        <div
          style={{
            position: 'fixed',
            ...chatPos,
            width: `${chatW}px`,
            height: `${chatH}px`,
            display: 'flex',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
            zIndex: 9999,
            overflow: 'hidden',
            background: t.fundo,
            transition: 'width 0.25s ease',
          }}
        >
          {/* ── Sidebar de conversas ── */}
          {mostrarSidebar && (
            <div
              style={{
                width: 200,
                flexShrink: 0,
                background: t.fundoSidebar,
                borderRight: `1px solid ${t.borda}`,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Header da sidebar */}
              <div style={{ padding: '12px 10px', borderBottom: `1px solid ${t.borda}` }}>
                <button
                  onClick={novaConversa}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #e10600, #ff3b30)',
                    border: 'none',
                    borderRadius: 8,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    padding: '8px 10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                  }}
                >
                  <span style={{ fontSize: 15, lineHeight: 1 }}>＋</span> Nova conversa
                </button>
              </div>

              {/* Lista de conversas */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {conversas.length === 0 && (
                  <div style={{ padding: '20px 12px', fontSize: 12, color: t.textoTerciario, textAlign: 'center', lineHeight: 1.5 }}>
                    Nenhuma conversa salva ainda.
                  </div>
                )}
                {conversas.map(conv => (
                  <div
                    key={conv.id}
                    onClick={() => abrirConversa(conv.id)}
                    onMouseEnter={() => setHoveredConversaId(conv.id)}
                    onMouseLeave={() => setHoveredConversaId(null)}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      background: conversaAtualId === conv.id
                        ? 'rgba(225,6,0,0.15)'
                        : hoveredConversaId === conv.id
                          ? t.hoverItem
                          : 'transparent',
                      borderLeft: conversaAtualId === conv.id ? '3px solid #e10600' : '3px solid transparent',
                      position: 'relative',
                      transition: 'background 0.12s',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: conversaAtualId === conv.id ? 600 : 400,
                        color: conversaAtualId === conv.id ? '#ff3b30' : t.textoSecundario,
                        lineHeight: 1.4,
                        paddingRight: 20,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical' as any,
                      }}
                    >
                      {conv.titulo}
                    </div>
                    <div style={{ fontSize: 10, color: t.textoTerciario, marginTop: 3 }}>
                      {formatarData(conv.atualizado_em)}
                    </div>
                    {hoveredConversaId === conv.id && (
                      <button
                        onClick={e => excluirConversa(conv.id, e)}
                        title="Excluir conversa"
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 6,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: t.textoSecundario,
                          fontSize: 13,
                          padding: 2,
                          lineHeight: 1,
                          borderRadius: 4,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#e10600')}
                        onMouseLeave={e => (e.currentTarget.style.color = t.textoSecundario)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Área principal do chat ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <div
              style={{
                background: 'linear-gradient(135deg, #e10600 0%, #ff3b30 100%)',
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                  <img src="/bravo-ai-avatar.png" alt="Bravo AI" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>Bravo AI</div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>Assistente da Bravo.Bet</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {/* Botão histórico */}
                <button
                  onClick={() => setMostrarSidebar(prev => !prev)}
                  title="Histórico de conversas"
                  style={{
                    background: mostrarSidebar ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: 6,
                    color: '#fff',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                    <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
                  </svg>
                </button>
                {/* Botão nova conversa (visível apenas sem sidebar) */}
                {!mostrarSidebar && (
                  <button
                    onClick={novaConversa}
                    title="Nova conversa"
                    style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '4px 8px', fontSize: 11, fontWeight: 600 }}
                  >
                    + Nova
                  </button>
                )}
                <button
                  onClick={() => setAberto(false)}
                  style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '4px 8px', fontSize: 16, lineHeight: 1, display: 'flex' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Mensagens */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                background: t.fundoMensagens,
                minHeight: '300px',
              }}
            >
              {carregandoConversa && (
                <div style={{ textAlign: 'center', color: t.textoTerciario, marginTop: 40, fontSize: 13 }}>
                  Carregando conversa...
                </div>
              )}

              {!carregandoConversa && mensagens.length === 0 && (
                <div style={{ textAlign: 'center', color: t.textoTerciario, marginTop: 40, fontSize: 14, lineHeight: 1.6 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>👋</div>
                  <div style={{ fontWeight: 600, color: t.textoSecundario, marginBottom: 4 }}>Olá! Sou a Bravo AI.</div>
                  <div>Pergunte sobre processos, tire dúvidas ou diga <strong>"quero abrir um card"</strong> para criar uma tarefa por aqui.</div>
                </div>
              )}

              {!carregandoConversa && mensagens.map((msg, i) => {
                const parsed = msg.role === 'assistant' ? parseMensagem(msg.content) : null;
                const parsedForm = msg.role === 'assistant' ? parseFormulario(parsed ? parsed.texto : msg.content) : null;
                const isUltimaTarefa = i === ultimaTarefaIdx;

                return (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: 8 }}>
                    <div
                      style={{
                        maxWidth: '85%',
                        padding: '10px 14px',
                        borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        background: msg.role === 'user' ? 'linear-gradient(135deg, #e10600, #ff3b30)' : t.bolhaAssistente,
                        color: msg.role === 'user' ? '#fff' : t.texto,
                        fontSize: 13.5,
                        lineHeight: 1.55,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                        wordBreak: 'break-word',
                      }}
                    >
                      {renderMarkdown(parsedForm ? parsedForm.texto : (parsed ? parsed.texto : msg.content))}

                      {/* Botão de download de formulário */}
                      {parsedForm?.nomeFormulario && (
                        <FormularioDownloadButton nomeFormulario={parsedForm.nomeFormulario} />
                      )}
                    </div>

                    {/* Card de confirmação de tarefa */}
                    {parsed?.tarefa && isUltimaTarefa && tarefaCriada !== 'ok' && (
                      <div
                        style={{
                          width: '100%',
                          background: t.cardFundo,
                          border: `1.5px solid ${t.cardBorda}`,
                          borderRadius: 12,
                          padding: '14px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#ff3b30', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Confirmar criação da tarefa
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 13, color: t.texto, marginBottom: 12 }}>
                          <div><span style={{ color: t.textoTerciario, fontSize: 11 }}>TÍTULO</span><br /><strong>{parsed.tarefa.titulo}</strong></div>
                          {parsed.tarefa.descricao && (
                            <div><span style={{ color: t.textoTerciario, fontSize: 11 }}>DESCRIÇÃO</span><br />{parsed.tarefa.descricao}</div>
                          )}
                          <div style={{ display: 'flex', gap: 16 }}>
                            <div><span style={{ color: t.textoTerciario, fontSize: 11 }}>PRIORIDADE</span><br />{prioridadeLabel[parsed.tarefa.prioridade] || parsed.tarefa.prioridade}</div>
                            <div><span style={{ color: t.textoTerciario, fontSize: 11 }}>RESPONSÁVEL</span><br />{parsed.tarefa.responsavel_nome}</div>
                          </div>
                          {parsed.tarefa.data_vencimento && (
                            <div><span style={{ color: t.textoTerciario, fontSize: 11 }}>VENCIMENTO</span><br />{parsed.tarefa.data_vencimento.split('-').reverse().join('/')}</div>
                          )}
                        </div>

                        {parsed.tarefa.quer_anexar && (
                          <div style={{ marginBottom: 10 }}>
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              style={{ display: 'none' }}
                              onChange={e => setArquivosSelecionados(Array.from(e.target.files || []))}
                            />
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              style={{
                                width: '100%',
                                background: t.inputFundo,
                                border: `1.5px dashed ${t.inputBorda}`,
                                borderRadius: 8,
                                color: t.textoSecundario,
                                fontSize: 12,
                                padding: '8px',
                                cursor: 'pointer',
                                marginBottom: 6,
                              }}
                            >
                              📎 {arquivosSelecionados.length > 0
                                ? `${arquivosSelecionados.length} arquivo(s) selecionado(s)`
                                : 'Clique para selecionar arquivos'}
                            </button>
                            {arquivosSelecionados.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {arquivosSelecionados.map((f, idx) => (
                                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: t.textoSecundario, background: t.chipFundo, borderRadius: 6, padding: '3px 8px' }}>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '85%' }}>📄 {f.name}</span>
                                    <button
                                      onClick={() => setArquivosSelecionados(prev => prev.filter((_, fi) => fi !== idx))}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ff3b30', fontSize: 13, padding: 0, flexShrink: 0, display: 'flex' }}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {tarefaCriada?.startsWith('erro:') && (
                          <div style={{ background: 'rgba(225,6,0,0.15)', border: '1px solid #e10600', borderRadius: 8, padding: '8px 10px', marginBottom: 10, fontSize: 12, color: '#ff3b30' }}>
                            ⚠️ {tarefaCriada.replace('erro:', '')}
                          </div>
                        )}

                        <button
                          onClick={() => confirmarCriarTarefa(parsed.tarefa!)}
                          disabled={criandoTarefa}
                          style={{
                            width: '100%',
                            background: criandoTarefa ? t.botaoNeutro : 'linear-gradient(135deg, #e10600, #ff3b30)',
                            border: 'none',
                            borderRadius: 8,
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 13,
                            padding: '10px',
                            cursor: criandoTarefa ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {criandoTarefa ? '⏳ Criando tarefa...' : '✅ Criar Tarefa'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {carregando && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{ padding: '10px 16px', borderRadius: '16px 16px 16px 4px', background: t.bolhaAssistente, boxShadow: '0 1px 4px rgba(0,0,0,0.12)', display: 'flex', gap: 5, alignItems: 'center' }}>
                    {[0, 1, 2].map(n => (
                      <div key={n} style={{ width: 7, height: 7, borderRadius: '50%', background: '#e10600', animation: 'bravoAiBounce 1.2s infinite', animationDelay: `${n * 0.2}s` }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div style={{ padding: 12, background: t.fundo, borderTop: `1px solid ${t.borda}`, display: 'flex', gap: 8, alignItems: 'flex-end', flexShrink: 0 }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Digite sua mensagem..."
                rows={1}
                style={{
                  flex: 1,
                  border: `1.5px solid ${t.inputBorda}`,
                  borderRadius: 10,
                  padding: '9px 12px',
                  fontSize: 13.5,
                  outline: 'none',
                  resize: 'none',
                  fontFamily: 'inherit',
                  lineHeight: 1.4,
                  maxHeight: 80,
                  overflowY: 'auto',
                  transition: 'border-color 0.2s',
                  background: t.inputFundo,
                  color: t.texto,
                }}
                onFocus={e => (e.target.style.borderColor = '#e10600')}
                onBlur={e => (e.target.style.borderColor = t.inputBorda)}
              />
              <button
                onClick={enviarMensagem}
                disabled={!input.trim() || carregando}
                style={{
                  background: input.trim() && !carregando ? 'linear-gradient(135deg, #e10600, #ff3b30)' : t.botaoNeutro,
                  border: 'none',
                  borderRadius: 10,
                  color: '#fff',
                  cursor: input.trim() && !carregando ? 'pointer' : 'not-allowed',
                  padding: '9px 14px',
                  fontSize: 16,
                  transition: 'background 0.2s',
                  flexShrink: 0,
                }}
              >
                ➤
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Botão flutuante arrastável */}
      <button
        onMouseDown={handleDragStart}
        title="Bravo AI — arraste para mover"
        style={{
          position: 'fixed',
          ...(pos ? { left: pos.x, top: pos.y } : { bottom: 24, right: 24 }),
          height: 48,
          paddingLeft: 16,
          paddingRight: 16,
          borderRadius: 24,
          background: 'linear-gradient(135deg, #e10600 0%, #ff3b30 100%)',
          border: 'none',
          boxShadow: '0 4px 16px rgba(225,6,0,0.45)',
          cursor: 'grab',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          userSelect: 'none',
          transition: 'box-shadow 0.2s',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 20px rgba(225,6,0,0.6)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 16px rgba(225,6,0,0.45)';
        }}
      >
        {aberto ? (
          <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#fff">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
            BRAVO AI
          </span>
        )}
      </button>

      <style>{`
        @keyframes bravoAiBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default BravoAI;
